import React, { FC } from 'react';
import { useTranslation } from 'Shared/Hooks/useTranslation';
import styled from 'styled-components';

import { Container, Row, Col } from 'crehana-ui/GridV2';
import StateTag from 'crehana-ui/Tag/StateTag';
import Star from 'crehana-icons/Star';
import User from 'crehana-icons/User';
import { yellowMain, secondaryMain } from 'crehana-ui/styles/colors';

import { Course } from '../../types';
import locales from '../../i18n/localeEs';

const translations = locales.es;

const formatDate = date => {
  const d = new Date(date);
  const monthIndex =
    d.getMonth() + 1 < 10 ? `0${d.getMonth() + 1}` : d.getMonth() + 1;
  const year = d.getFullYear();

  return `${monthIndex}/${year}`;
};

type Type = 'BEST_SELLER' | 'NEW';

const renderTag = (type: Type, t) => {
  if (type === 'BEST_SELLER') {
    return (
      <StateTag
        className="mr-24"
        darkMode={undefined}
        label={t('BEST_SELLER', {
          defaultValue: translations.BEST_SELLER,
        })}
        type="yellow"
      />
    );
  }
  if (type === 'NEW') {
    return (
      <StateTag
        className="mr-24"
        darkMode={undefined}
        label={t('NEW', {
          defaultValue: translations.NEW,
        })}
        type="green"
      />
    );
  }

  return null;
};

const StickyMainSectionWrapper = styled.div<{ isSticky }>`
  z-index: 10;
  top: 0;
  transition: transform 0.3s ease;
  transform: ${({ isSticky }) =>
    isSticky ? 'translateY(0px)' : 'translateY(-100%)'};
`;

type StickyMainSectionProp = {
  course: Course;
  isSticky: boolean;
};

const StickyMainSection: FC<StickyMainSectionProp> = ({ course, isSticky }) => {
  const { t } = useTranslation();

  const Reviews = (
    <p className="flex items-center">
      <Star color={yellowMain} size={20} />
      <span className="font-caption ml-4">{course.score.average}</span>
      <span className="ml-4 font-legals text-gray-dark">
        ({course.score.total})
      </span>
    </p>
  );

  const enrollmentsVisibility = course.enrollments > 10;

  const Enrollments = (
    <p className="flex ml-16 items-center">
      <User size={20} color={secondaryMain} />
      <span className="ml-4 text-12 font-nun font-bold">
        {course.enrollments.toLocaleString()}{' '}
        {t('STUDENTS', {
          defaultValue: translations.STUDENTS,
        })}
      </span>
    </p>
  );

  return (
    <StickyMainSectionWrapper
      isSticky={isSticky}
      className="fixed w-full hidden md:block bg-gray-lighter lg:p-24 py-12"
    >
      <Container>
        <Row>
          <Col
            xs={12}
            sm={10}
            md={6}
            lg={8}
            offset={{ xs: 0, sm: 1, md: 0, lg: 0 }}
          >
            <h2 className="font-h4 mb-16">
              <span className="mr-4">
                {' '}
                {t('SEO_TITLE', {
                  defaultValue: translations.SEO_TITLE,
                })}
              </span>
              <span> {course.title}</span>
            </h2>
            <div className="flex items-center">
              <div className="sm:flex sm:items-center ">
                {course.courseTag && renderTag(course.courseTag.name, t)}
                <div className="flex items-center mt-24 mb-16 sm:mb-0 sm:mt-0">
                  {Reviews}
                  {enrollmentsVisibility && Enrollments}
                </div>
              </div>
              <p className="hidden sm:inline-flex font-body4 text-gray-dark sm:mt-0 sm:ml-16">
                {t('UPDATED_AT', {
                  defaultValue: translations.UPDATED_AT,
                })}{' '}
                {formatDate(course.modifiedDate)}
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </StickyMainSectionWrapper>
  );
};

export default StickyMainSection;
